import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { AscImportReport } from '../../../utils/timetable/timetableVersion';
import type { AscImportLabels } from '../hooks/useAscImportLabels';
import styles from './AscImportOverviewSlide.module.scss';

type AscImportOverviewSlideProps = {
  report: AscImportReport;
  labels: AscImportLabels;
  isPreview: boolean;
};

/** Erste Folie: was der Import bewirkt, plus alles, was nicht an eine Art gebunden ist. */
export const AscImportOverviewSlide: FC<AscImportOverviewSlideProps> = ({ report, labels, isPreview }) => {
  const { t } = useTranslation();

  // Die Vorschau zeigt alle Kennzahlen als Kacheln; der fertige Bericht nur einen Satz dazu, wie
  // viele Zuordnungen angelegt wurden (die übrigen Kennzahlen gehören in die Vorschau).
  const summary: { value: number; label: string }[] = [
    { value: report.cardsImported, label: labels.summary.cardsImported },
    { value: report.cardsInFile, label: t('pinboard.actions.ascImportReport.cardsInFile') },
    { value: report.createdLessons, label: labels.summary.createdLessons },
    { value: report.createdCards, label: labels.summary.createdCards },
    { value: report.createdDivisions, label: labels.summary.createdDivisions },
    { value: report.createdGroups, label: labels.summary.createdGroups },
    { value: report.linkedEntities, label: labels.summary.linkedEntities },
    ...(report.mode === 'FRESH'
      ? [
          { value: report.removedCards, label: labels.summary.removedCards },
          { value: report.removedLessons, label: labels.summary.removedLessons },
        ]
      : []),
  ];

  return (
    <div className={styles.content}>
      {isPreview && <div className={styles['section-hint']}>{t('pinboard.actions.ascImportPreview.hint')}</div>}

      {isPreview ? (
        <div className={styles.summary}>
          {summary.map((item) => (
            <div className={styles['summary-item']} key={item.label}>
              <span className={styles['summary-value']}>{item.value}</span>
              <span className={styles['summary-label']}>{item.label}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles['result-text']}>
          {t('pinboard.actions.ascImportReport.createdLessonsText', { count: report.createdLessons })}
        </div>
      )}

      {isPreview && report.unresolved.length > 0 && (
        <div className={styles.section}>
          <div className={styles['section-title']}>
            {t('pinboard.actions.ascImportReport.unresolvedTitle', { count: report.unresolved.length })}
          </div>
          <div className={styles['section-hint']}>{t('pinboard.actions.ascImportMapping.hint')}</div>
        </div>
      )}

      {isPreview && report.warnings.length > 0 && (
        <div className={styles.section}>
          <div className={styles['section-title']}>{t('pinboard.actions.ascImportReport.warningsTitle')}</div>
          <ul className={styles.warnings}>
            {report.warnings.map((warning) => (
              <li className={styles.warning} key={warning}>
                {warning}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
